import { useState, useEffect } from 'react';
import {
  Avatar,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  InputAdornment,
  Link
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';

const Login = ({ open, onClose }) => {
  const { login, user, error: authError, isLoading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendEmailSent, setResendEmailSent] = useState(false);

  useEffect(() => {
    if (user && open) {
      onClose();
    }
  }, [user, open, onClose]);

  const handleResendConfirmation = async () => {
    if (!email) {
      setError('이메일을 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) throw error;

      setResendEmailSent(true);
      setError('');
    } catch (error) {
      console.error('이메일 재발송 오류:', error);
      setError('이메일 재발송 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('이메일과 비밀번호를 입력해주세요.');
      return;
    }
    
    setLoading(true);
    setError('');
    setResendEmailSent(false);
    
    try {
      await login(email, password);
    } catch (error) {
      console.error('Login error:', error);
      if (error.message?.includes('Email not confirmed') || error.message?.includes('email_verified')) {
        setError('이메일 인증이 필요합니다.');
      } else if (error.message?.includes('Invalid login credentials')) {
        setError('이메일 또는 비밀번호가 올바르지 않습니다.');
      } else {
        setError(error.message || '로그인 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography component="h1" variant="h5">
            로그인
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            mt: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ width: '100%', mb: 2 }}
              action={
                error.includes('이메일 인증') && (
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={handleResendConfirmation}
                    disabled={loading || resendEmailSent}
                  >
                    {resendEmailSent ? '발송됨' : '재발송'}
                  </Button>
                )
              }
            >
              {error}
              {error.includes('이메일 인증') && !resendEmailSent && (
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                  인증 이메일을 받지 못하셨나요?
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleResendConfirmation}
                    sx={{ ml: 1 }}
                  >
                    여기를 클릭하여 재발송
                  </Link>
                </Typography>
              )}
              {resendEmailSent && (
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  인증 이메일이 재발송되었습니다. 이메일을 확인해주세요.
                </Typography>
              )}
            </Alert>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="이메일 주소"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="비밀번호"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : '로그인'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default Login; 